/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { Store } from '../../core/store/Store';
import { ChatModel } from '../../models/components/ChatModel';
import { Student } from '../../types/Student';
import { Message } from '../../types/Message';

export class ChatStore extends Store {
	private chatModel: ChatModel = new ChatModel();

	public isActiveChat(key: string): boolean {
		return this.chatModel.activeChat === key;
	}

	public getActiveChat(): string {
		return this.chatModel.activeChat;
	}

	public setActiveChat(key: string): void {
		this.chatModel.setActiveChat(key);
	}

	public setStudents(students: Student[]): void {
		this.chatModel.setStudents(students);
	}

	public getStudents(): Student[] {
		return this.chatModel.students;
	}

	public setAreStudentsBeingFetched(bool: boolean): void {
		this.chatModel.setAreStudentsBeingFetched(bool);
	}

	public areStudentsBeingFetched(): boolean {
		return this.chatModel.areStudentsBeingFetched;
	}

	public setMessages(key: string, messages: Message[]): void {
		this.chatModel.setMessages(key, messages);
	}

	public getMessages(): { [key: string]: Message[] } {
		return this.chatModel.messages;
	}

	public getMessageIdentifiers(): string[] {
		return this.chatModel.messageIdentifiers;
	}

	public addMessageIdentifier(identifier: string): void {
		this.chatModel.addMessageIdentifier(identifier);
	}

	public getUnreadMessages(): string[] {
		return this.chatModel.unreadMessageIdentifiers;
	}

	public getAllUnreadMessageAmounts(): { [key: string]: number } {
		return this.chatModel.unreadMessagesAmount;
	}

	public getUnreadMessagesAmount(key: string): number {
		return this.chatModel.unreadMessagesAmount[key] ?? 0;
	}

	public addUnreadMessage(identifier: string, key: string): void {
		this.chatModel.addUnreadMessage(identifier, key);
	}

	public resetUnreadMessages(key: string): void {
		this.chatModel.resetUnreadMessages(key);
	}

	public setAreMessagesBeingFetched(key: string, bool: boolean): void {
		this.chatModel.setAreMessagesBeingFetched(key, bool);
	}

	public areMessagesBeingFetched(): boolean {
		return this.chatModel.messagesLoaded;
	}

	public setMessage(message: string): void {
		this.chatModel.setMessage(message);
	}

	public getMessage(): string {
		return this.chatModel.message;
	}

	public setMessageToDelete(identifier: string): void {
		this.chatModel.setMessageToDelete(identifier);
	}

	public getMessageToDelete(): string {
		return this.chatModel.messageToDelete;
	}

	public get reload(): boolean {
		return this.chatModel.reload;
	}

	public set reload(reload: boolean) {
		this.chatModel.reload = reload;
	}
}
